import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const AUTO_LIFT_MS = 7000;
const FADE_MS = 3000;

// Elegant thin vertical stem with a single restrained ivy curl → downward arrowhead.
function IvyArrow({ className = "" }) {
  return (
    <svg
      viewBox="0 0 40 160"
      width="40"
      height="160"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M20 0 L20 140" />
      <path d="M20 42 C 12 46, 8 54, 12 62 C 15 68, 22 66, 22 60" />
      <path d="M12 62 C 6 66, 6 74, 12 76 C 14 72, 15 68, 12 62 Z" />
      <path d="M20 92 C 26 96, 28 102, 25 108 C 22 112, 18 110, 19 106" />
      <path d="M14 132 L20 142 L26 132" />
    </svg>
  );
}

/**
 * SplashCurtain. Parent owns dismiss lifecycle (so it can crossfade the site in).
 * `dismissing`: when true, the curtain fades to 0 over FADE_MS then unmounts.
 */
export function SplashCurtain({ dismissing, onDismiss }) {
  const [enter, setEnter] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    // Enter fade-in on next paint.
    const raf = requestAnimationFrame(() => setEnter(true));
    // Auto-lift.
    const timer = setTimeout(() => onDismiss?.(), AUTO_LIFT_MS);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      await api.post("/waitlist", { email, role: "launch" });
      setSent(true);
      toast.success("You're on the list.");
    } catch {
      toast.error("Something went wrong. Try again?");
    } finally {
      setSending(false);
    }
  };

  const opacity = dismissing ? 0 : enter ? 1 : 0;

  return (
    <div
      data-testid="splash-curtain"
      className="fixed inset-0 z-[100] overflow-hidden text-[#E8E4D9]"
      style={{
        opacity,
        transition: `opacity ${FADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        pointerEvents: dismissing ? "none" : "auto",
      }}
    >
      {/* Full-bleed atmospheric image */}
      <div className="absolute inset-0">
        <img
          src="/splash-1.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/70" />
        <div
          className="absolute inset-0 opacity-[0.09] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='1'/></svg>\")",
          }}
        />
      </div>

      <div className="relative z-10 h-full flex flex-col items-center justify-between px-6 py-14 sm:py-20 text-center">
        <div />

        {/* Center block — original elegant scale */}
        <div className="max-w-2xl">
          <h1 className="font-display font-light tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Grace Under Umbrella
          </h1>
          <p className="font-display italic text-white/75 text-xl sm:text-2xl mt-6">
            A tender shelter.
          </p>
          <p className="overline text-white/60 mt-10">Opening soon</p>

          <p className="font-display italic text-white/70 text-base sm:text-lg leading-relaxed mt-14 max-w-md mx-auto">
            Rooted in a quiet storm, restored by what grows.
          </p>

          <form
            onSubmit={submit}
            data-testid="splash-notify-form"
            className="mt-12 max-w-md mx-auto"
          >
            {sent ? (
              <div className="font-display italic text-white/80 text-lg">
                Thank you. We'll write when the greenhouse opens.
              </div>
            ) : (
              <div className="flex items-end border-b border-white/25 focus-within:border-white/70 transition-colors">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your email"
                  data-testid="splash-email-input"
                  className="flex-1 bg-transparent outline-none border-0 py-3 text-base placeholder:text-white/40"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="splash-notify-submit"
                  className="overline text-white/80 hover:text-white pl-4 pb-3 transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Notify me →"}
                </button>
              </div>
            )}
          </form>
        </div>

        <button
          onClick={() => onDismiss?.()}
          data-testid="splash-enter-btn"
          aria-label="Enter the greenhouse"
          className="group flex flex-col items-center gap-3 text-white/55 hover:text-white/90 transition-colors"
        >
          <IvyArrow className="opacity-80 group-hover:opacity-100 transition-opacity" />
          <span className="overline">Enter the greenhouse</span>
        </button>
      </div>

      <div className="absolute bottom-4 left-0 right-0 text-center text-white/40 text-[0.65rem] tracking-widest-2 uppercase z-10">
        20% of profit goes to a foundation to be named
      </div>
    </div>
  );
}

export function shouldShowSplash() {
  // Suppression removed: splash plays on every homepage load / refresh / return.
  return true;
}

export function markSplashSeen() {
  // No-op. Suppression removed intentionally — splash must replay every home visit.
}

export const SPLASH_FADE_MS = FADE_MS;
